import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CustomerDetailsSection } from '../../../resources/base/models/enums';
import { deduplicateStringsArray } from '../../common/miscUtils';

const initialState = {
  customerData: {} as CreditIntegration.Schemas.GetApplicantDetailsCustomerData['customerData'],
  customerDataCoCustomer: {} as CreditIntegration.Schemas.GetApplicantDetailsCustomerData['customerData'],
  submittedForms: [] as CustomerDetailsSection[],
  creditApplicationDetails: {} as CreditIntegration.Schemas.CreditApplicationResponse,
  expandedForm: undefined as CustomerDetailsSection | undefined,
  isCreditApplicationDataReady: false,
  canRedirectToOnfido: true,
};

type CreditApplicationState = typeof initialState;
export type CreditApplicantCustomerData = CreditIntegration.Schemas.GetApplicantDetailsCustomerData['customerData'];

export const creditApplicationSlice = createSlice({
  name: 'creditApplication',
  initialState,
  selectors: {
    selectCanRedirectToOnfido: (state: CreditApplicationState) => {
      return state.canRedirectToOnfido;
    },
    selectCreditApplicantCustomerData: (state: CreditApplicationState) => {
      return state.customerData;
    },
    selectCreditApplicantCustomerDataCoCustomer: (state: CreditApplicationState) => {
      return state.customerDataCoCustomer;
    },

    selectSubmittedForms: (state: CreditApplicationState) => {
      return state.submittedForms;
    },

    selectCreditApplicationIds: (state: CreditApplicationState) => {
      const details = state.creditApplicationDetails;

      return {
        quoteId: details.financialProduct?.offers[0]?.offerId,
        creditApplicationId: details.creditDecision?.solvencyCheck?.decisionIdentifier,
        applicantCreditScoreId: details.customerData?.externalIdentifiers?.[0]?.id,
        applicantCreditScoreIdCoCustomer: details.customerData?.customerRelations?.[0].externalIdentifiers?.[0]?.id,
      };
    },

    selectCreditApplicationDetails: (state: CreditApplicationState) => {
      return state.creditApplicationDetails;
    },

    selectExpandedFormName: (state: CreditApplicationState) => {
      return state.expandedForm;
    },

    selectIsSoleTraderFromCreditApplication: (state: CreditApplicationState) => {
      const entityLegalForm = (
        state.customerData as
          | CreditIntegration.Schemas.UpdateLegalEntityCustomerDataResponse
          | CreditIntegration.Schemas.UpdateSoleTraderCustomerDataResponse
          | undefined
      )?.companyData?.entityLegalForm;
      return entityLegalForm === 'EI' || entityLegalForm === 'INDIV_PP';
    },
    selectIsCreditApplicationDataReady: (state: CreditApplicationState) => {
      return state.isCreditApplicationDataReady;
    },

    selectCustomerTypeFromCreditApplication: (state: CreditApplicationState) => {
      return state.customerData.customerType;
    },
  },
  reducers: {
    setCreditApplicantCustomerData(
      state,
      { payload }: PayloadAction<CreditIntegration.Schemas.GetApplicantDetailsCustomerData>
    ) {
      state.customerData = payload.customerData;
    },
    setCreditApplicationDetails(
      state,
      { payload }: PayloadAction<CreditIntegration.Schemas.CreditApplicationResponse>
    ) {
      state.creditApplicationDetails = payload;
    },
    setCreditApplicantCustomerDataCoCustomer(
      state,
      { payload }: PayloadAction<CreditIntegration.Schemas.GetApplicantDetailsCustomerData>
    ) {
      state.customerDataCoCustomer = payload.customerData;
    },
    setFormAsSubmitted(state, { payload }: PayloadAction<CustomerDetailsSection>) {
      state.submittedForms = deduplicateStringsArray([...state.submittedForms, payload]);
    },
    setFormAsNotSubmitted(state, { payload }: PayloadAction<CustomerDetailsSection>) {
      state.submittedForms = [...state.submittedForms.filter((formName) => formName !== payload)];
    },
    setSubmittedForms(state, { payload }: PayloadAction<CustomerDetailsSection[]>) {
      state.submittedForms = [...payload];
    },
    setExpandedForm(state, { payload }: PayloadAction<CustomerDetailsSection | undefined>) {
      state.expandedForm = payload;
    },
    setIsCreditApplicationDataReady(state) {
      state.isCreditApplicationDataReady = true;
    },
    setCanRedirectToOnfido(state, { payload }) {
      state.canRedirectToOnfido = payload;
    },
    resetCreditApplication() {
      return initialState;
    },
  },
});

export const creditApplicationSelectors = creditApplicationSlice.selectors;
export const {
  setCreditApplicantCustomerData,
  setCreditApplicantCustomerDataCoCustomer,
  setCreditApplicationDetails,
  setExpandedForm,
  setFormAsNotSubmitted,
  setFormAsSubmitted,
  setIsCreditApplicationDataReady,
  setSubmittedForms,
  setCanRedirectToOnfido,
  resetCreditApplication,
} = creditApplicationSlice.actions;
export const creditApplicationReducer = creditApplicationSlice.reducer;
